import { useEffect, useState, type ReactNode } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Collapse,
  Divider,
  Group,
  Loader,
  MultiSelect,
  RingProgress,
  ScrollArea,
  Select,
  Skeleton,
  Stack,
  Switch,
  Text,
  Textarea,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconSparkles,
  IconUserCheck,
  IconUserOff,
} from '@tabler/icons-react';
import { trpc } from '../../lib/trpc';
import { timeAgo } from '../../lib/chat-format';

/** Cross-channel identity fields a contact may carry (WhatsApp contacts have a
 *  waId; FB/IG contacts have an IG username / scoped id / FB page-scoped id).
 *  Mirrors the helpers in chat.tsx so the panel renders an identity for
 *  non-WhatsApp contacts whose waId is null. */
type ContactIdentity = {
  fullName?: string | null;
  waId?: string | null;
  instagramUsername?: string | null;
  instagramScopedId?: string | null;
  facebookPageScopedId?: string | null;
};

function contactDisplayName(c: ContactIdentity): string {
  return (
    c.fullName ||
    c.waId ||
    c.instagramUsername ||
    c.instagramScopedId ||
    c.facebookPageScopedId ||
    ''
  );
}

function contactIdentifier(c: ContactIdentity): string | null {
  return c.waId ?? c.instagramUsername ?? c.facebookPageScopedId ?? null;
}

type SectionKey =
  | 'status'
  | 'assignment'
  | 'bots'
  | 'labels'
  | 'groups'
  | 'notes'
  | 'summary'
  | 'custom'
  | 'activity';

/** The fixed set of "summarize last N messages" counts the panel offers —
 *  mirrors `SUMMARY_COUNT_OPTIONS` in `apps/api/src/modules/chat/schema.ts`. */
const SUMMARY_COUNT_OPTIONS = [5, 10, 20, 30, 50, 100] as const;

/**
 * Right-hand contact / CRM panel. Channel-agnostic: driven by a Contact id via
 * `chat.openingBundle`, which already returns FB/IG identifiers and null
 * window/opt-in fields for non-WhatsApp contacts. Pass `hideWhatsAppOnly` for
 * the Messenger/Instagram inboxes to drop the WhatsApp-only Status (opt-in /
 * 24h window) section and the WhatsApp block action; every other section
 * (assignment, bot routing, labels, groups, notes, custom fields, activity) is
 * generic to any Contact.
 */
export function CrmPanel({
  contactId,
  hideWhatsAppOnly = false,
}: {
  contactId: number;
  hideWhatsAppOnly?: boolean;
}) {
  const bundle = trpc.chat.openingBundle.useQuery({ contactId });
  const labels = trpc.chat.allLabels.useQuery();
  const groups = trpc.chat.allGroups.useQuery();
  const assignableUsers = trpc.chat.assignableUsers.useQuery();
  const utils = trpc.useUtils();
  const refresh = () => {
    void utils.chat.openingBundle.invalidate({ contactId });
    void utils.chat.contacts.invalidate();
    // Mirror to the standalone /vendor/contacts list — same row in two
    // surfaces, two tRPC namespaces, so a chat-side mutation has to flush
    // the contacts page's cache or it shows stale assignment / labels.
    void utils.contacts.contacts.list.invalidate();
  };
  const updateLabels = trpc.chat.updateContactLabels.useMutation({ onSuccess: refresh });
  const updateGroups = trpc.chat.updateContactGroups.useMutation({ onSuccess: refresh });
  const updateNotes = trpc.chat.updateContactNotes.useMutation({ onSuccess: refresh });
  const setBlocked = trpc.chat.setContactBlocked.useMutation({ onSuccess: refresh });
  const assignUser = trpc.chat.assignTeamMember.useMutation({ onSuccess: refresh });
  const setBotEnabled = trpc.chat.setContactBotEnabled.useMutation({ onSuccess: refresh });
  const generateSummary = trpc.chat.generateContactSummary.useMutation({ onSuccess: refresh });
  const setDefaultCount = trpc.chat.setSummaryDefaultCount.useMutation({ onSuccess: refresh });

  const [notes, setNotes] = useState('');
  const [labelIds, setLabelIds] = useState<string[]>([]);
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [summaryCount, setSummaryCount] = useState<(typeof SUMMARY_COUNT_OPTIONS)[number]>(20);
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>({
    status: true,
    assignment: true,
    bots: false,
    labels: true,
    groups: false,
    notes: true,
    summary: true,
    custom: false,
    activity: false,
  });

  useEffect(() => {
    if (bundle.data) {
      setNotes(bundle.data.contact.notes ?? '');
      setLabelIds(bundle.data.contact.labels.map((l) => String(l.id)));
      setGroupIds(bundle.data.contact.groups.map((g) => String(g.id)));
      setSummaryCount(
        (SUMMARY_COUNT_OPTIONS as readonly number[]).includes(bundle.data.summaryDefaultCount)
          ? (bundle.data.summaryDefaultCount as (typeof SUMMARY_COUNT_OPTIONS)[number])
          : 20,
      );
    }
  }, [bundle.data]);

  if (bundle.isPending) {
    return (
      <Card style={{ height: '100%' }}>
        <Skeleton height="100%" />
      </Card>
    );
  }
  if (!bundle.data) return null;

  const { contact } = bundle.data;
  const toggle = (k: SectionKey) => setOpenSections((p) => ({ ...p, [k]: !p[k] }));

  const isHealthy =
    !contact.isOptedOut &&
    !contact.isBlocked &&
    !contact.isAiBotDisabled &&
    !contact.isReplyBotDisabled &&
    contact.isWindowOpen;

  return (
    <Card p={0} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Stack
        p="md"
        gap={8}
        align="center"
        style={{
          background:
            'linear-gradient(180deg, light-dark(var(--mantine-color-blue-0), var(--mantine-color-dark-5)) 0%, transparent 100%)',
        }}
      >
        <Avatar size={64} radius="xl">
          {(contactDisplayName(contact) || '?')[0]?.toUpperCase()}
        </Avatar>
        <Stack gap={0} align="center">
          <Text fw={600}>{contactDisplayName(contact) || '—'}</Text>
          <Text c="dimmed" size="xs" ff="monospace">
            {contactIdentifier(contact) ?? '—'}
          </Text>
          {contact.email && (
            <Text c="dimmed" size="xs">
              {contact.email}
            </Text>
          )}
        </Stack>
        {contact.leadScore != null && (
          <Group gap="xs" mt={4} wrap="nowrap">
            <RingProgress
              size={48}
              thickness={4}
              sections={[
                {
                  value: contact.leadScore,
                  color:
                    contact.leadScore > 70 ? 'green' : contact.leadScore > 40 ? 'yellow' : 'gray',
                },
              ]}
              label={
                <Text ta="center" fw={700} size="xs">
                  {contact.leadScore}
                </Text>
              }
            />
            <Box>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Lead score
              </Text>
              <Text size="xs">
                {contact.leadScore > 70 ? 'Hot' : contact.leadScore > 40 ? 'Warm' : 'Cold'}
                {contact.lastMessageAt
                  ? ` · last active ${timeAgo(new Date(contact.lastMessageAt))} ago`
                  : ''}
              </Text>
            </Box>
          </Group>
        )}
      </Stack>

      <Divider />

      <ScrollArea style={{ flex: 1 }}>
        <Stack gap={0} p={0}>
          {!hideWhatsAppOnly && (
            <CrmSection title="Status" open={openSections.status} onToggle={() => toggle('status')}>
              <Group gap={6} wrap="wrap">
                {isHealthy && (
                  <Badge color="blue" variant="light">
                    healthy
                  </Badge>
                )}
                {contact.isWindowOpen ? (
                  <Badge color="green" variant="light">
                    window open
                  </Badge>
                ) : (
                  <Badge color="orange" variant="light">
                    window closed
                  </Badge>
                )}
                {contact.isOptedOut && (
                  <Badge color="orange" variant="light">
                    opted out
                  </Badge>
                )}
                {contact.isBlocked && (
                  <Badge color="red" variant="light">
                    blocked
                  </Badge>
                )}
                {contact.isAiBotDisabled && <Badge variant="light">AI bot off</Badge>}
                {contact.isReplyBotDisabled && <Badge variant="light">reply bot off</Badge>}
              </Group>
            </CrmSection>
          )}

          <CrmSection
            title="Assignment"
            open={openSections.assignment}
            onToggle={() => toggle('assignment')}
          >
            <Select
              placeholder="Unassigned"
              clearable
              searchable
              nothingFoundMessage="No matches"
              data={(assignableUsers.data ?? []).map((u) => ({ value: u.id, label: u.name }))}
              value={contact.assignedUserId ?? null}
              onChange={(v) => assignUser.mutate({ contactId, userId: v })}
              size="sm"
            />
          </CrmSection>

          <CrmSection title="Bot routing" open={openSections.bots} onToggle={() => toggle('bots')}>
            <Stack gap={6}>
              <Switch
                label="AI bot replies"
                description="Allow the AI bot to auto-reply to this contact"
                checked={!contact.isAiBotDisabled}
                onChange={(e) =>
                  setBotEnabled.mutate({
                    contactId,
                    bot: 'ai',
                    enabled: e.currentTarget.checked,
                  })
                }
              />
              <Switch
                label="Keyword bot replies"
                description="Allow keyword bot rules to fire for this contact"
                checked={!contact.isReplyBotDisabled}
                onChange={(e) =>
                  setBotEnabled.mutate({
                    contactId,
                    bot: 'reply',
                    enabled: e.currentTarget.checked,
                  })
                }
              />
            </Stack>
          </CrmSection>

          <CrmSection title="Labels" open={openSections.labels} onToggle={() => toggle('labels')}>
            <MultiSelect
              data={(labels.data ?? []).map((l) => ({
                value: String(l.id),
                label: l.title,
              }))}
              value={labelIds}
              onChange={(vals) => {
                setLabelIds(vals);
                updateLabels.mutate({ contactId, labelIds: vals.map(Number) });
              }}
              searchable
              placeholder="Add labels…"
              size="sm"
            />
          </CrmSection>

          <CrmSection title="Groups" open={openSections.groups} onToggle={() => toggle('groups')}>
            <MultiSelect
              data={(groups.data ?? []).map((g) => ({
                value: String(g.id),
                label: g.title,
              }))}
              value={groupIds}
              onChange={(vals) => {
                setGroupIds(vals);
                updateGroups.mutate({ contactId, groupIds: vals.map(Number) });
              }}
              searchable
              placeholder="Add to groups…"
              size="sm"
            />
          </CrmSection>

          <CrmSection title="Notes" open={openSections.notes} onToggle={() => toggle('notes')}>
            <Textarea
              minRows={3}
              autosize
              placeholder="Free-form notes about this contact"
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
              onBlur={() =>
                updateNotes.mutate(
                  { contactId, notes: notes || null },
                  {
                    onSuccess: () =>
                      notifications.show({
                        color: 'green',
                        message: 'Notes saved',
                        autoClose: 1200,
                      }),
                    onError: (err) => notifications.show({ color: 'red', message: err.message }),
                  },
                )
              }
              size="sm"
              rightSection={
                updateNotes.isPending ? (
                  <Loader size="xs" />
                ) : updateNotes.isSuccess ? (
                  <IconCheck size={14} color="var(--mantine-color-green-6)" />
                ) : null
              }
              aria-label="Contact notes"
            />
          </CrmSection>

          <CrmSection
            title="Summary"
            open={openSections.summary}
            onToggle={() => toggle('summary')}
          >
            <Stack gap={8}>
              <Group gap={6} wrap="nowrap">
                <Select
                  data={SUMMARY_COUNT_OPTIONS.map((n) => ({ value: String(n), label: String(n) }))}
                  value={String(summaryCount)}
                  onChange={(v) => {
                    if (v) setSummaryCount(Number(v) as (typeof SUMMARY_COUNT_OPTIONS)[number]);
                  }}
                  size="sm"
                  w={80}
                  allowDeselect={false}
                  aria-label="Number of messages to summarize"
                />
                <Text size="xs" c="dimmed">
                  messages
                </Text>
                {summaryCount !== bundle.data.summaryDefaultCount && (
                  <Button
                    variant="subtle"
                    size="compact-xs"
                    loading={setDefaultCount.isPending}
                    onClick={() =>
                      setDefaultCount.mutate(
                        { count: summaryCount },
                        {
                          onError: (err) =>
                            notifications.show({ color: 'red', message: err.message }),
                        },
                      )
                    }
                  >
                    Set as default
                  </Button>
                )}
              </Group>
              <Button
                size="xs"
                variant="light"
                leftSection={<IconSparkles size={14} />}
                loading={generateSummary.isPending}
                onClick={() =>
                  generateSummary.mutate(
                    { contactId, messageCount: summaryCount },
                    {
                      onError: (err) => notifications.show({ color: 'red', message: err.message }),
                    },
                  )
                }
              >
                Generate summary
              </Button>
              {contact.summary.text ? (
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                  {contact.summary.text}
                </Text>
              ) : (
                <Text size="xs" c="dimmed" fs="italic">
                  No summary yet — pick a count and click Generate.
                </Text>
              )}
              {contact.summary.generatedAt && (
                <Text size="xs" c="dimmed">
                  Last updated {timeAgo(new Date(contact.summary.generatedAt))} ago
                  {contact.summary.messageCount != null
                    ? ` · based on last ${contact.summary.messageCount} messages`
                    : ''}
                </Text>
              )}
            </Stack>
          </CrmSection>

          {contact.customFieldValues.length > 0 && (
            <CrmSection
              title="Custom fields"
              open={openSections.custom}
              onToggle={() => toggle('custom')}
            >
              <Stack gap={4}>
                {contact.customFieldValues.map((f) => (
                  <Group key={f.id} justify="space-between" wrap="nowrap" gap="xs">
                    <Text size="xs" c="dimmed" truncate>
                      {f.name || `Field #${f.id}`}
                    </Text>
                    <Text size="xs" truncate style={{ maxWidth: 180 }}>
                      {f.value || '—'}
                    </Text>
                  </Group>
                ))}
              </Stack>
            </CrmSection>
          )}

          {contact.recentActivity && contact.recentActivity.length > 0 && (
            <CrmSection
              title="Recent activity"
              open={openSections.activity}
              onToggle={() => toggle('activity')}
            >
              <Stack gap={6}>
                {contact.recentActivity.map((a, i) => {
                  // Click → scroll to + briefly flash the corresponding message
                  // bubble in the conversation thread. If the message isn't on
                  // the currently loaded page the scroll falls through to no-op.
                  const jump = () => {
                    const el = document.getElementById(`activity-msg-${a.messageLogId}`);
                    if (!el) return;
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    el.classList.add('activity-flash');
                    setTimeout(() => el.classList.remove('activity-flash'), 1500);
                  };
                  return (
                    <Group
                      key={i}
                      gap={8}
                      wrap="nowrap"
                      align="flex-start"
                      onClick={jump}
                      style={{ cursor: 'pointer' }}
                    >
                      <Box
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          background: 'var(--mantine-color-blue-5)',
                          flexShrink: 0,
                          marginTop: 6,
                        }}
                      />
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Text size="xs" fw={500} truncate>
                          {a.title}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {a.subtitle} · {timeAgo(new Date(a.at))} ago
                        </Text>
                      </Box>
                    </Group>
                  );
                })}
              </Stack>
            </CrmSection>
          )}
        </Stack>
      </ScrollArea>

      {!hideWhatsAppOnly && (
        <>
          <Divider />
          <Box p="sm">
            <Button
              fullWidth
              variant="light"
              color={contact.isBlocked ? 'green' : 'red'}
              leftSection={
                contact.isBlocked ? <IconUserCheck size={14} /> : <IconUserOff size={14} />
              }
              onClick={() => setBlocked.mutate({ contactId, blocked: !contact.isBlocked })}
            >
              {contact.isBlocked ? 'Unblock contact' : 'Block contact'}
            </Button>
          </Box>
        </>
      )}
    </Card>
  );
}

function CrmSection({
  title,
  open,
  onToggle,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <Box>
      <UnstyledButton
        onClick={onToggle}
        style={{
          width: '100%',
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderTop: '1px solid var(--mantine-color-gray-2)',
        }}
      >
        <Text size="xs" fw={600} tt="uppercase" c="gray.7" style={{ letterSpacing: 0.4 }}>
          {title}
        </Text>
        {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
      </UnstyledButton>
      <Collapse expanded={open}>
        <Box px="md" pb="md">
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

/** Placeholder shown in the right-panel slot for an FB/IG conversation that
 *  isn't linked to a CRM Contact yet (so there's no `contactId` to drive the
 *  panel). Keeps the column width stable instead of collapsing the layout. */
export function CrmPanelEmpty() {
  return (
    <Card p="md" style={{ height: '100%' }}>
      <Center style={{ height: '100%' }}>
        <Text c="dimmed" size="sm" ta="center" px="md">
          No CRM contact is linked to this conversation yet.
        </Text>
      </Center>
    </Card>
  );
}
